import { Check, Loader2, TriangleAlert, X } from 'lucide-react';
import { useState } from 'react';

import { isApiError } from '@/lib/http/api-error';
import { cn } from '@/lib/utils';

import { asistenteApi } from '../asistente.api';
import type { AccionSpec } from '../asistente.types';

interface AssistantActionProps {
  accion: AccionSpec;
}

type Estado = 'pendiente' | 'ejecutando' | 'hecho' | 'error' | 'cancelada';

/**
 * Propuesta de acción del asistente.
 *
 * El asistente nunca escribe por su cuenta: aquí se muestra qué haría y el
 * cambio sólo ocurre si el usuario pulsa «Confirmar», que llama a
 * `POST /api/asistente_accion`. Así el usuario autoriza siempre el cambio y el
 * backend lo registra en la auditoría.
 */
export function AssistantAction({ accion }: AssistantActionProps) {
  const [estado, setEstado] = useState<Estado>('pendiente');
  const [mensaje, setMensaje] = useState<string | null>(null);

  const ejecutar = async () => {
    setEstado('ejecutando');

    try {
      const resultado = await asistenteApi.ejecutarAccion({ id: accion.id, params: accion.params });

      setMensaje(resultado?.mensaje ?? 'Acción realizada correctamente.');
      setEstado('hecho');
    } catch (error) {
      setMensaje(
        isApiError(error) ? error.message : 'No se pudo ejecutar la acción. Inténtalo de nuevo.',
      );
      setEstado('error');
    }
  };

  const cerrada = estado === 'hecho' || estado === 'error' || estado === 'cancelada';

  return (
    <section
      className={cn(
        'mt-3 rounded-xl border p-3',
        estado === 'hecho'
          ? 'border-emerald-200 bg-emerald-50'
          : estado === 'error'
            ? 'border-rose-200 bg-rose-50'
            : 'border-amber-200 bg-amber-50',
      )}
      aria-label="Acción propuesta por el asistente"
    >
      <header className="flex items-start gap-2">
        <TriangleAlert
          className={cn(
            'mt-0.5 size-4 shrink-0',
            estado === 'hecho' ? 'text-emerald-600' : estado === 'error' ? 'text-rose-600' : 'text-amber-600',
          )}
          aria-hidden="true"
        />

        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink-900">
            {estado === 'hecho' ? 'Acción realizada' : accion.titulo}
          </p>
          <p className="mt-0.5 text-xs text-ink-600">{accion.detalle}</p>

          {accion.advertencia && estado === 'pendiente' ? (
            <p className="mt-1 text-xs text-amber-700">{accion.advertencia}</p>
          ) : null}

          {mensaje ? <p className="mt-1 text-xs font-medium text-ink-800">{mensaje}</p> : null}

          {estado === 'cancelada' ? (
            <p className="mt-1 text-xs text-ink-500">Acción descartada: no se cambió nada.</p>
          ) : null}
        </div>
      </header>

      {!cerrada ? (
        <div className="mt-2.5 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={ejecutar}
            disabled={estado === 'ejecutando'}
            className={cn(
              'inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5',
              'text-xs font-semibold text-white transition-colors hover:bg-brand-700',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            {estado === 'ejecutando' ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <Check className="size-3.5" aria-hidden="true" />
            )}
            Confirmar
          </button>

          <button
            type="button"
            onClick={() => setEstado('cancelada')}
            disabled={estado === 'ejecutando'}
            className={cn(
              'inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5',
              'text-xs font-medium text-ink-600 transition-colors hover:bg-ink-50',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            <X className="size-3.5" aria-hidden="true" />
            Cancelar
          </button>
        </div>
      ) : null}
    </section>
  );
}
